import axios from "axios";
import React, { useState, useEffect, useRef, useCallback } from "react";
import Email from "../components/email";
import { renderToStaticMarkup } from "react-dom/server";

let quoteSent = false;

export default function QuoteStep({
	quoteStep,
	quoteDetails,
	setQuoteDetails,
	email,
	zapierLink,
	thankYouPage,
	handleStartAgain,
}) {
	const [heading, setHeading] = useState("");
	const [description, setDescription] = useState("");
	const [imgFallback, setImgFallback] = useState(false);
	const [fallbackTimeout, setFallbackTimeout] = useState(false);
	const imgRef = useRef(null);

	useEffect(() => {
		const { heading, description } = quoteStep;

		setHeading(heading);
		setDescription(description);
		calculateQuote();
	}, [calculateQuote, quoteStep]);

	const calculateQuote = useCallback(() => {
		const marqueePrice = parseInt(quoteDetails.marquee.value);
		const optionsValues = quoteDetails.marqueeOptions.map((option) =>
			parseInt(option.value)
		);
		const optionPrice = optionsValues.reduce((a, b) => a + b, 0);
		const totalPrice =
			marqueePrice + optionPrice + quoteDetails.distancePrice;
		const VATPrice = (totalPrice + totalPrice * 0.2).toFixed(2);

		setQuoteDetails({
			quoteId:
				`${quoteDetails.postcode}-${quoteDetails.lastName}-${quoteDetails.partyDate}`.toUpperCase(),
			price: totalPrice,
			VATPrice: VATPrice,
			priceSet: true,
		});
	}, [quoteDetails, setQuoteDetails]);

	const handleEmailQuote = useCallback(() => {
		if (fallbackTimeout) {
			setQuoteDetails({
				choosenMarqueeURL: imgRef.current.src,
				emailHTML: renderToStaticMarkup(
					<Email
						quoteDetails={quoteDetails}
						email={email}
						marqueeImg={imgRef.current.src}
					/>
				),
				quoteComplete: true,
			});
		} else {
			setTimeout(() => {
				setFallbackTimeout(true);
			}, 2000);
		}
	}, [fallbackTimeout, setQuoteDetails, quoteDetails, email]);

	useEffect(() => {
		if (quoteDetails.priceSet && !quoteDetails.quoteComplete) {
			handleEmailQuote();
		} else if (!quoteSent && quoteDetails.quoteComplete) {
			axios({
				method: "post",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
				url: zapierLink,
				data: quoteDetails,
			}).then((res) => {
				if (res.status === 200) {
					setTimeout(() => {
						window.location = thankYouPage;
					}, 500);
				}
			});

			quoteSent = true;
		}
	}, [handleEmailQuote, quoteDetails, thankYouPage, zapierLink]);

	const getMarqueeImg = useCallback(() => {
		let id = "";
		let fl = "";
		let mrq = "";

		if (
			["3m x 4m", "3m x 6m", "4m x 4m", "4m x 6m"].includes(
				quoteDetails.marquee.size
			)
		) {
			mrq = "4x6";
		} else {
			mrq = "4x12";
		}

		if (quoteDetails.marqueeOptions.length > 0) {
			id = "-";

			quoteDetails.marqueeOptions.forEach((option) => {
				if (option.optionName === "Full Lining") {
					fl = "-FL";
				}

				if (typeof option.id !== "undefined") {
					id += option.id;
				}
			});
		}

		return `${mrq}${fl}${id}`;
	}, [quoteDetails]);

	const onImgError = useCallback(() => {
		if (!imgFallback) {
			imgRef.current.src = `https://www.partyinyourgarden.co.uk/wp-content/plugins/quote-calculator/assets/static/images/quote-images/fallback/${quoteDetails.partyType.replace(
				/\s/g,
				"-"
			)}.jpg`;
			setImgFallback(true);
		} else {
			imgRef.current.style.display = "none";
			document.querySelector(".quote-details__img-info").style.display =
				"none";
		}
	}, [quoteDetails, imgRef, imgFallback]);

	const quoteDetailsCard = useCallback(() => {
		const { marquee, marqueeOptions, price, VATPrice } = quoteDetails;

		return (
			<ul className="quote-details--left">
				<p className="quote-details__marquee-size">
					<strong>{marquee.size}</strong> PVC Marquee
				</p>

				<p className="quote-details__category">Standard Options</p>
				<li className="quote-details__detail">1x Carpet, Dark Grey</li>
				<li className="quote-details__detail">
					1x Hard Flooring System
				</li>
				<li className="quote-details__detail">
					1x White Marquee Roof Lining
				</li>

				{marqueeOptions.length > 0 && (
					<p className="quote-details__category">Selected Options</p>
				)}
				{marqueeOptions.map((option, index) => (
					<li className="quote-details__detail" key={index}>
						{option.quantity}x {option.optionName}
						<span>£{option.value}</span>
					</li>
				))}

				<p className="quote-details__price">
					£{price}
					<small> (+VAT)</small>
				</p>
				<p className="quote-details__price-vat">£{VATPrice}</p>

				<hr />
			</ul>
		);
	}, [quoteDetails]);

	const buildPaymentLink = useCallback(() => {
		const { quoteId, email, contactNumber, firstName, lastName, postcode } =
			quoteDetails;
		const queryString = `?quote_id=${quoteId}&billing_email=${email}&billing_first_name=${firstName}&billing_last_name=${lastName}&billing_phone=${contactNumber}&shipping_postcode=${postcode}`;

		return `https://www.partyinyourgarden.co.uk/checkouts/olq-deposit-marquee-hire-checkout/${queryString}`;
	}, [quoteDetails]);

	return (
		<>
			<div className="quote-step" style={{ display: "none" }}>
				<div className="quote-step__section">
					<div className="quote-step__info-container">
						<h4>
							{heading.replace(
								"<event>",
								quoteDetails.partyType === "Other"
									? ""
									: quoteDetails.partyType
							)}
						</h4>
						<p>{description}</p>
					</div>

					<div className="quote-step__quote-details">
						{quoteDetailsCard()}

						<div className="quote-details--right">
							<p className="quote-details__img-info">
								{imgFallback
									? quoteStep.marquee_image_fallback_info
									: quoteStep.marquee_image_info}
							</p>

							<img
								className="quote-step__marquee-img"
								src={`https://www.partyinyourgarden.co.uk/wp-content/plugins/quote-calculator/assets/static/images/quote-images/${getMarqueeImg()}.jpg`}
								ref={imgRef}
								alt=""
								onError={onImgError}
								onLoad={handleEmailQuote}
							/>

							<div className="quote-step__btn-group">
								<a
									className="quote-step__pay-btn"
									href={buildPaymentLink()}
								>
									Pay £100 Deposit Online
								</a>
								<button
									onClick={handleStartAgain}
									className="quote-calculator__back-btn"
								>
									Start Again
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="loader">
				<div className="loader-inner"></div>
				<div className="loader-triangle"></div>
			</div>
		</>
	);
}
